import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Layout, PhotoMarker } from '../types/types';
import ZoomControls from './ZoomControls';
import ModeControls from './ModeControls';
import MeasurementControls from './MeasurementControls';
import PhotoIndexControls from './PhotoIndexControls';
import { downloadLayoutImage } from '../utils/downloadLayoutImage';
import { downloadLayoutPDF } from '../utils/downloadLayoutPDF';
import { useState } from 'react';
import { useUser } from '@/shared/hooks/useUser';

interface TopControlsProps {
  selectedLayout: string;
  setSelectedLayout: (layout: string) => void;
  layouts: Layout[];
  loadLayout: (layout: Layout) => void;
  zoom: (factor: number) => void;
  reset: () => void;
  mode: string;
  setMode: (mode: 'view' | 'measure' | 'photoIndex' | 'areaTags') => void;
  photoIndexMode: boolean;
  setCurrentPolygon: (polygon: number[][]) => void;
  closePolygon: () => void;
  clearPolygon: () => void;
  savePhotoMarkers: () => void;
  setPhotoIndexMode: (value: boolean) => void;
  setSelectedMarkers: (markers: Set<number>) => void;
  setShowMarkerToolbar: (value: boolean) => void;
  svgRef: React.RefObject<SVGSVGElement>;
  isPdfLayout: boolean;
  pdfPath: string | null;
  photoMarkers: PhotoMarker[];
  scale: number;
}


const TopControls = ({
  selectedLayout,
  setSelectedLayout,
  layouts,
  loadLayout,
  zoom,
  reset,
  mode,
  setMode,
  photoIndexMode,
  setCurrentPolygon,
  closePolygon,
  clearPolygon,
  savePhotoMarkers,
  setPhotoIndexMode,
  setSelectedMarkers,
  setShowMarkerToolbar,
  svgRef,
  isPdfLayout,
  pdfPath,
  photoMarkers,
  scale,
}: TopControlsProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { user } = useUser();
  
  // Determine which controls to show based on user role
  const isAdminOrSuperuser = user?.is_staff || user?.is_superuser;
  const isSuperuser = Boolean(user?.is_superuser);
  const showZoomControls = true;
  const showModeControls = isAdminOrSuperuser;
  const showMeasurementControls = false;
  const showPhotoIndexControls = isAdminOrSuperuser;

  const handleDownloadLayout = async () => {
    setIsDownloading(true);
    try {
      const layout = layouts.find((l) => l.id === selectedLayout);
      const fileName = layout?.name 
        ? `${layout.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-with-markers`
        : 'layout-with-markers';
      
      // Use PDF download for PDF layouts, image download for image layouts
      if (isPdfLayout && pdfPath) {
        await downloadLayoutPDF(pdfPath, photoMarkers, scale, fileName);
      } else {
        await downloadLayoutImage(svgRef, fileName);
      }
    } catch (error) {
      console.error('Failed to download layout:', error);
      alert('Failed to download layout. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-card border-b border-border p-4">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <Label htmlFor="layoutSelect" className="text-sm font-medium whitespace-nowrap">
            Layout:
          </Label>
          <Select
            value={selectedLayout}
            onValueChange={(value) => {
              setSelectedLayout(value);
              const layout = layouts.find((l) => String(l.id) === value);
              if (layout) loadLayout(layout);
            }}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Select layout..." />
            </SelectTrigger>
            <SelectContent>
              {layouts
                ?.slice()
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
                .map((layout) => (
                  <SelectItem key={layout.id} value={String(layout.id)}>
                    {layout.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          {showZoomControls && <ZoomControls zoom={zoom} reset={reset} />}
          {showModeControls && (
            <ModeControls 
              mode={mode} 
              photoIndexMode={photoIndexMode}
              canEditAreaTags={isSuperuser}
              setMode={setMode}
              setPhotoIndexMode={setPhotoIndexMode}
              setShowMarkerToolbar={setShowMarkerToolbar}
              onDownload={handleDownloadLayout}
              isDownloading={isDownloading}
            />
          )}

          {mode === 'measure' && showMeasurementControls && (
            <MeasurementControls
              setCurrentPolygon={setCurrentPolygon}
              closePolygon={closePolygon}
              clearPolygon={clearPolygon}
            />
          )}

          {photoIndexMode && showPhotoIndexControls && (
            <PhotoIndexControls
              savePhotoMarkers={savePhotoMarkers}
              setPhotoIndexMode={setPhotoIndexMode}
              setSelectedMarkers={setSelectedMarkers}
              setShowMarkerToolbar={setShowMarkerToolbar}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TopControls;
